import { useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ConnectionHandler } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { useMedicalReportUpload } from "@/modules/patients/services/useMedicalReportUpload"
import { UPLOAD_CONFIGS } from "@/modules/upload/constants/upload-configs"
import { DocumentTypeSelector } from "@/modules/upload/document-type-selector"
import { DropzoneSection } from "@/modules/upload/dropzone-section"
import { UploadFormData, UploadFormInput, UploadSchema } from "@/modules/upload/schema/upload-schema"
import { useUploadMutation } from "@/modules/upload/services/useUploadMutation"
import { Country } from "@/types/__generated__/union-types"

interface UploadFormProps {
  appointmentId?: string
  progressNoteId?: string
  country?: Country
  onClose: () => void
}

export const UploadForm = ({ appointmentId, progressNoteId, country, onClose }: UploadFormProps) => {
  const {
    t,
    i18n: { exists },
  } = useTranslation()
  const [errorMessage, setErrorMessage] = useState<string | null | undefined | Error>(null)
  const [errorFileName, setErrorFileName] = useState<string>()
  const [appointmentAttachmentsUpload] = useUploadMutation()
  const [medicalReportAttachmentsUpload] = useMedicalReportUpload(country)
  const form = useForm<UploadFormInput, unknown, UploadFormData>({
    defaultValues: { documentType: "MEDICAL_REPORT", files: [] },
    criteriaMode: "all",
    mode: "all",
    resolver: zodResolver(UploadSchema),
  })

  if (!appointmentId && !progressNoteId) {
    return null
  }

  const {
    formState: { isSubmitting, isSubmitSuccessful, isValid },
    handleSubmit,
    reset,
  } = form

  const handleAppointmentSubmitValid: SubmitHandler<z.infer<typeof UploadSchema>> = async (data) => {
    if (!appointmentId) throw new Error("There is no appointment id")

    setErrorMessage(null)

    return new Promise((resolve, reject) => {
      appointmentAttachmentsUpload({
        appointmentId,
        files: data.files,
        handleCompleted(attachments) {
          if (attachments && attachments.length > 0) {
            reset()
            onClose()
            resolve(attachments)
          }
        },
        handleError(errorMessage, fileName) {
          setErrorMessage(errorMessage)
          if (fileName) setErrorFileName(fileName)

          reject(errorMessage instanceof Error ? errorMessage : new Error(errorMessage))
        },
      })
    })
  }

  const handleProgressNoteSubmitValid: SubmitHandler<z.infer<typeof UploadSchema>> = async (data) => {
    if (!progressNoteId) throw new Error("There is no progress note id")

    const connectionId = ConnectionHandler.getConnectionID(
      progressNoteId,
      "ProgressNoteConnection_medicalReportAttachments",
    )

    return new Promise((resolve, reject) => {
      medicalReportAttachmentsUpload({
        addConnections: [connectionId],
        files: data.files,
        progressNoteId,
        reportType: data.documentType,
        onCompleted() {
          setErrorMessage(null)
          reset()
          onClose()
          resolve(true)
        },
        onError(errorMessage) {
          setErrorMessage(errorMessage)
          reject(errorMessage instanceof Error ? errorMessage : new Error(errorMessage))
        },
      })
    })
  }

  const handleFormReset = () => {
    if (isSubmitSuccessful) {
      reset()
    }
  }

  return (
    <div className="mx-6 my-2 mb-6 grid gap-4 overflow-hidden">
      <ul className="ml-2">
        <li className="text-on-surface-subdued before:bg-on-surface-subdued relative indent-3 text-sm before:absolute before:top-2 before:left-0 before:size-1 before:rounded-full">
          {t("patient.uploadFiles.upToNumberFiles", {
            fileCount: UPLOAD_CONFIGS.MAX_FILE,
            fileSize: UPLOAD_CONFIGS.MAX_FILE_SIZE_IN_MB,
          })}
        </li>
        <li className="text-on-surface-subdued before:bg-on-surface-subdued relative indent-3 text-sm before:absolute before:top-2 before:left-0 before:size-1 before:rounded-full">
          {t("patient.uploadFiles.supportsFileTypes", {
            fileTypes: Object.values(UPLOAD_CONFIGS.ACCEPT_FILE_TYPES)
              .flat()
              .map((item) => item.replace(".", ""))
              .join(", "),
          })}
        </li>
      </ul>
      <div className="grid gap-8">
        <FormProvider {...form}>
          <form
            className="grid gap-6"
            onSubmit={handleSubmit(progressNoteId ? handleProgressNoteSubmitValid : handleAppointmentSubmitValid)}>
            <div className="grid gap-4">
              {progressNoteId && <DocumentTypeSelector />}
              <DropzoneSection
                isUploading={isSubmitting}
                errorFileName={errorFileName}
                onFileDialogOpen={handleFormReset}
                onDragEnter={handleFormReset}
                onDropzoneChange={() => {
                  if (errorMessage && !errorFileName) setErrorMessage(null)
                }}
                onRemoveSuccessful={(file) => {
                  if (!errorFileName) return null
                  else if (errorMessage && errorFileName) setErrorMessage(null)

                  if (errorFileName === file.name) setErrorFileName(undefined)
                }}
              />
              {isSubmitSuccessful && (
                <Banner type="inline" variant="success">
                  {t("patient.uploadFiles.uploadSuccessful")}
                </Banner>
              )}
              {errorMessage && (
                <Banner type="inline" variant="critical">
                  {exists(`patient.uploadFiles.errors.${errorMessage}`)
                    ? t(`patient.uploadFiles.errors.${errorMessage}`)
                    : t("patient.uploadFiles.errorUploadingFile")}
                </Banner>
              )}
            </div>
            <div className="ml-auto flex space-x-2 divide-x">
              <Button variant="secondary" data-testid="cancel-button" onClick={onClose}>
                {t("common.button.cancel")}
              </Button>
              <Button
                className="w-24"
                data-testid="upload-button"
                disabled={(!appointmentId && !progressNoteId) || !isValid}
                isLoading={isSubmitting}>
                {t("common.button.upload")}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
